import { useEffect, useRef, useState } from "react";
import { salonInfo } from "@/mocks/content";

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
);
const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
  </svg>
);
const MapPinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);

export default function InfoSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const infoItems = [
    { icon: <ClockIcon />, label: "営業時間", value: salonInfo.hours.map(h => `${h.day} ${h.time}`).join(" / ") },
    { icon: <CalendarIcon />, label: "定休日", value: salonInfo.closedDay },
    { icon: <UsersIcon />, label: "個室数", value: `${salonInfo.seats}室（完全個室）` },
    { icon: <PhoneIcon />, label: "電話番号", value: salonInfo.phone, isPhone: true },
    { icon: <MapPinIcon />, label: "住所", value: salonInfo.address },
  ];

  return (
    <section id="info" ref={sectionRef} className="py-24 md:py-40 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-start">
          {/* Left: Sticky Image */}
          <div
            className="lg:sticky lg:top-24"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(-30px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <div className="w-full aspect-[4/5] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=luxury%20mens%20spa%20reception%20area%20with%20warm%20wooden%20counter%2C%20soft%20ambient%20lighting%2C%20minimalist%20japanese%20interior%20design%2C%20fresh%20flowers%20in%20vase%2C%20clean%20and%20serene%20atmosphere%2C%20warm%20beige%20and%20charcoal%20tones%2C%20no%20people&width=800&height=1000&seq=info1&orientation=portrait"
                alt="サロン受付"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right: Info */}
          <div>
            <p className="text-[#8B7355] text-xs tracking-[0.4em] mb-3 font-light" style={{ fontFamily: "'Noto Serif JP', serif", opacity: visible ? 1 : 0, transition: "opacity 0.6s ease 0.2s" }}>
              SALON INFO
            </p>
            <h2
              className="text-[#2C2C2C] font-light mb-12 leading-tight"
              style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "clamp(1.8rem,3.5vw,3rem)", opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s" }}
            >
              店舗情報
            </h2>

            <div className="flex flex-col gap-0 border-l border-[#E8E6E1]">
              {infoItems.map((item, i) => (
                <div
                  key={item.label}
                  className="flex items-start gap-5 py-6 pl-6 border-b border-[#E8E6E1]"
                  style={{ opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(20px)", transition: `opacity 0.6s ease ${0.4 + i * 0.1}s, transform 0.6s ease ${0.4 + i * 0.1}s` }}
                >
                  <div className="w-5 h-5 flex items-center justify-center text-[#8B7355] mt-0.5 flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-[#8B7355] text-xs tracking-[0.2em] mb-1 font-light" style={{ fontFamily: "'Noto Serif JP', serif" }}>{item.label}</p>
                    {item.isPhone ? (
                      <a href={`tel:${item.value}`} className="text-[#2C2C2C] font-light text-base cursor-pointer hover:text-[#8B7355] transition-colors" style={{ fontFamily: "'Noto Serif JP', serif" }}>{item.value}</a>
                    ) : (
                      <p className="text-[#2C2C2C] font-light text-sm leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
